import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios, { API } from '../services/api';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sun, Plus, X, CaretRight, Spinner, Calendar, CalendarBlank, Clock, MapPin, CaretLeft, ListBullets, SquaresFour, FilePdf, CalendarPlus, Sparkle
} from '@phosphor-icons/react';
import { MainLayout } from '../components/shared/MainLayout';
import { useSWRCache, peekSWRCache } from '../services/useSWRCache';

const downloadIcs = (e, appointmentId, title) => {
  e.stopPropagation();
  const API = process.env.REACT_APP_BACKEND_URL + '/api';
  const link = document.createElement('a');
  link.href = `${API}/appointments/${appointmentId}/ics`;
  link.download = `kindra-${(title || 'appointment').replace(/\s+/g, '-').slice(0, 30)}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Wave 2 P3 — Appointment intelligence.
// Map a condition or medication name (lowercased) to a likely specialty.
// Conservative — only the high-confidence buckets so we never confidently
// suggest the wrong specialty. If nothing matches we fall through silently.
const SPECIALTY_HINTS = [
  { kw: ['thyroid', 'hashimoto', 'graves', 'hypothyroid', 'hyperthyroid', 'diabetes', 'levothyroxine', 'metformin', 'insulin', 'pcos'], specialty: 'Endocrinology' },
  { kw: ['hypertension', 'blood pressure', 'cholesterol', 'statin', 'atrial', 'angina', 'cardiac', 'arrhythmia'], specialty: 'Cardiology' },
  { kw: ['migraine', 'seizure', 'epilep', 'neuropath', 'parkinson', 'multiple sclerosis'], specialty: 'Neurology' },
  { kw: ['anxiety', 'depression', 'bipolar', 'adhd', 'ptsd', 'ssri', 'sertraline', 'fluoxetine', 'venlafaxine'], specialty: 'Psychiatry' },
  { kw: ['eczema', 'psoriasis', 'dermatitis', 'rosacea', 'acne'], specialty: 'Dermatology' },
  { kw: ['asthma', 'copd', 'sleep apnoea', 'sleep apnea'], specialty: 'Respiratory' },
  { kw: ['crohn', 'ibs', 'ibd', 'gerd', 'reflux', 'coeliac', 'celiac', 'ulcerative'], specialty: 'Gastroenterology' },
  { kw: ['arthritis', 'lupus', 'fibromyalgia', 'rheumatoid'], specialty: 'Rheumatology' },
  { kw: ['cancer', 'oncolog', 'chemo', 'tumour', 'tumor'], specialty: 'Oncology' },
];

const inferSpecialty = (conditions, teamMembers) => {
  // Start from team member roles (highest confidence — explicitly user-confirmed).
  const teamRoles = (teamMembers || []).map((m) => (m.role || '').toLowerCase()).filter(Boolean);
  for (const role of teamRoles) {
    for (const { kw, specialty } of SPECIALTY_HINTS) {
      if (kw.some((k) => role.includes(k))) return { specialty, source: `your ${specialty.toLowerCase()} team` };
    }
    // Direct role-name match against the dropdown list.
    const roleNorm = role.replace(/-/g, '');
    for (const opt of ['Cardiology', 'Neurology', 'Psychiatry', 'Dermatology', 'Endocrinology', 'Gastroenterology', 'Rheumatology', 'Oncology']) {
      if (roleNorm.includes(opt.toLowerCase())) return { specialty: opt, source: `your ${opt.toLowerCase()} team` };
    }
  }
  // Fall back to conditions.
  for (const c of conditions || []) {
    const name = (c?.name || '').toLowerCase();
    for (const { kw, specialty } of SPECIALTY_HINTS) {
      if (kw.some((k) => name.includes(k))) return { specialty, source: c.name };
    }
  }
  return null;
};

const AppointmentsPage = () => {
  const navigate = useNavigate();
  // Stage-15 P0 — cache-first hydration for the appointments list.
  const { data: apptData, refetch: refetchAppointments } = useSWRCache('/appointments', { ttlMs: 30_000 });
  const appointments = apptData || [];
  const [conditions, setConditions] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(apptData == null);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'calendar'
  const [showModal, setShowModal] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [formData, setFormData] = useState({ status: 'upcoming' });
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [saving, setSaving] = useState(false);

  // Cache-warmed: as soon as apptData hydrates from cache we drop the loading
  // skeleton. Background revalidation continues invisibly.
  useEffect(() => {
    if (apptData != null && loading) setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apptData]);

  // Conditions + team are non-blocking secondary fetches; the page is fully
  // useful before they land.
  useEffect(() => {
    (async () => {
      try {
        const [condRes, teamRes] = await Promise.all([
          axios.get(`${API}/conditions`),
          axios.get(`${API}/medical-team`).catch(() => ({ data: [] })),
        ]);
        setConditions(condRes.data);
        setTeamMembers((teamRes.data || []).filter(m => m.status !== 'past'));
      } catch { /* non-blocking */ }
    })();
  }, []);

  // Compatibility shim for downstream code.
  const fetchData = refetchAppointments;
  const setAppointments = () => { /* writes flow via API + refetchAppointments */ };

  // Wave 2 P3 — Appointment intelligence.
  // Compute a contextual hint from the user's conditions + care team. If we
  // find a confident match we surface the line "Based on your record, this
  // looks like an X follow-up" inside the modal so the user feels Kindra
  // already knows them. Recomputes when the underlying data changes.
  const specialtyHint = useMemo(
    () => inferSpecialty(conditions, teamMembers),
    [conditions, teamMembers],
  );

  // When the user opens the new-appointment modal with an empty form, smart-
  // prefill: specialty (from inference), and linked_conditions (auto-link the
  // single condition / all if there are 1-2). Never override user input.
  const openNewModal = () => {
    setFormData((prev) => {
      // Only prefill on a clean form (status is the default flag).
      const isFresh = !prev.title && !prev.specialty && !(prev.linked_conditions || []).length;
      if (!isFresh) return prev;
      const next = { status: 'upcoming' };
      if (specialtyHint?.specialty) next.specialty = specialtyHint.specialty;
      if ((conditions || []).length > 0 && (conditions || []).length <= 2) {
        next.linked_conditions = conditions.map((c) => c.name);
      } else if ((conditions || []).length > 0 && specialtyHint?.source && conditions.find((c) => c.name === specialtyHint.source)) {
        next.linked_conditions = [specialtyHint.source];
      }
      return next;
    });
    setShowModal(true);
  };

  const handleCreate = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/appointments`, formData);
      await fetchData();
      setShowModal(false);
      setFormData({ status: 'upcoming' });
      // Phase 7: activation handoff — if user came from /activation, complete the loop.
      try {
        const sp = new URLSearchParams(window.location.search);
        if (sp.get('from') === 'activation') {
          axios.post(`${API}/activation/track`, { event: 'first_appointment_completed', props: { source: 'activation_handoff' } }).catch(() => {});
          // Bounce into the activation success screen with an appointment-specific note.
          window.location.replace('/activation?completed=appointment');
          return;
        }
      } catch { /* no-op */ }
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'upcoming': return 'bg-blue-100 text-blue-700';
      case 'completed': return 'bg-green-100 text-green-700';
      case 'cancelled': return 'bg-red-100 text-red-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const upcomingAppts = appointments.filter(a => a.status === 'upcoming').sort((a, b) => new Date(a.date) - new Date(b.date));
  const pastAppts = appointments.filter(a => a.status !== 'upcoming').sort((a, b) => new Date(b.date) - new Date(a.date));

  // Calendar helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    return { firstDay, daysInMonth };
  };

  const getAppointmentsForDay = (day) => {
    const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return appointments.filter(a => a.date === dateStr);
  };

  const { firstDay, daysInMonth } = getDaysInMonth(currentMonth);

  return (
    <MainLayout>
      <header className="flex items-end justify-between mb-5 gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-[0.18em]">Care between visits</p>
          <h1
            className="font-serif text-[26px] sm:text-[30px] leading-tight tracking-[-0.01em] text-[#0E1C2E] font-normal mt-0.5"
            data-testid="appointments-title"
          >
            Your <em className="italic">appointments.</em>
          </h1>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Wave G-A · P0.5 — visible "+ Add appointment" CTA in the
              header. Desktop shows full label; mobile collapses to a "+"
              icon to stay thumb-friendly. The floating FAB stays as a
              secondary mobile shortcut on long lists. */}
          <button
            onClick={openNewModal}
            className="inline-flex items-center justify-center gap-1.5 rounded-full px-3 sm:px-4 py-2 min-w-[44px] min-h-[44px] bg-[#235347] text-white text-sm font-medium hover:bg-[#1A3F36] transition-colors"
            data-testid="add-appointment-header"
            aria-label="Add appointment"
          >
            <Plus size={16} weight="bold" />
            <span className="hidden sm:inline">Add appointment</span>
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2.5 min-w-[40px] min-h-[40px] flex items-center justify-center rounded-lg transition-colors ${viewMode === 'list' ? 'bg-[#0E1C2E] text-white' : 'bg-white border border-[#ECE8E1] text-slate-500 hover:border-[#CFC8BB]'}`}
            aria-label="List view"
          >
            <ListBullets size={18} />
          </button>
          <button
            onClick={() => setViewMode('calendar')}
            className={`p-2.5 min-w-[40px] min-h-[40px] flex items-center justify-center rounded-lg transition-colors ${viewMode === 'calendar' ? 'bg-[#0E1C2E] text-white' : 'bg-white border border-[#ECE8E1] text-slate-500 hover:border-[#CFC8BB]'}`}
            aria-label="Calendar view"
          >
            <SquaresFour size={18} />
          </button>
        </div>
      </header>

      {/* Prepare for appointment shortcut */}
      {appointments.length > 0 && (
        <div className="pt-2 pb-0">
          <button
            onClick={async () => {
              setExportingPdf(true);
              try {
                const response = await axios.get(`${API}/export/doctor-summary`, { responseType: 'blob' });
                const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                const link = document.createElement('a');
                link.href = url;
                link.download = `Kindra_Health_Summary_${new Date().toISOString().slice(0,10)}.pdf`;
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                toast.success('Summary downloaded — ready for your appointment');
              } catch { toast.error('Failed to generate summary'); }
              finally { setExportingPdf(false); }
            }}
            disabled={exportingPdf}
            className="w-full flex items-center gap-2 justify-center text-sm text-[#235347] font-medium py-2 rounded-lg border border-[#235347]/20 hover:bg-[#235347]/5 transition-colors disabled:opacity-50"
            data-testid="prepare-appointment-summary"
          >
            {exportingPdf ? <Spinner size={14} className="animate-spin" /> : <FilePdf size={14} weight="duotone" />}
            Prepare summary for appointment
          </button>
        </div>
      )}

      <div className="py-3">
        {loading ? (
          <div className="flex justify-center py-12">
            <Spinner size={32} className="animate-spin text-[#235347]" />
          </div>
        ) : viewMode === 'list' ? (
          <>
            {/* Next Appointment Highlight */}
            {upcomingAppts.length > 0 && (
              <div className="mb-6">
                <h2 className="text-sm font-medium text-slate-500 uppercase tracking-wide mb-3">Next Appointment</h2>
                <button
                  onClick={() => navigate(`/appointments/${upcomingAppts[0].id}`)}
                  className="w-full card-base p-5 text-left hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-medium text-slate-900 text-lg">{upcomingAppts[0].title}</h3>
                      <p className="text-slate-500">{upcomingAppts[0].clinician_name || upcomingAppts[0].specialty}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(upcomingAppts[0].status)}`}>
                      {upcomingAppts[0].status}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-slate-600">
                    <div className="flex items-center gap-1">
                      <CalendarBlank size={16} />
                      {new Date(upcomingAppts[0].date).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })}
                    </div>
                    {upcomingAppts[0].time && (
                      <div className="flex items-center gap-1">
                        <Clock size={16} />
                        {upcomingAppts[0].time}
                      </div>
                    )}
                    {upcomingAppts[0].clinic_name && (
                      <div className="flex items-center gap-1">
                        <MapPin size={16} />
                        {upcomingAppts[0].clinic_name}
                      </div>
                    )}
                  </div>
                  <div
                    onClick={(e) => downloadIcs(e, upcomingAppts[0].id, upcomingAppts[0].title)}
                    className="mt-3 flex items-center gap-1.5 text-xs font-medium text-[#235347] hover:text-[#1A3F36] transition-colors w-fit"
                    data-testid="next-appt-add-calendar"
                  >
                    <CalendarPlus size={14} weight="duotone" />
                    Add to Calendar
                  </div>
                </button>
              </div>
            )}

            {/* All Upcoming */}
            {upcomingAppts.length > 1 && (
              <div className="mb-6">
                <h2 className="text-sm font-medium text-slate-500 uppercase tracking-wide mb-3">Upcoming</h2>
                <div className="space-y-3">
                  {upcomingAppts.slice(1).map((appt) => (
                    <button
                      key={appt.id}
                      onClick={() => navigate(`/appointments/${appt.id}`)}
                      className="w-full card-base p-4 text-left hover:bg-slate-50 transition-colors flex items-center justify-between"
                    >
                      <div>
                        <h3 className="font-medium text-slate-900">{appt.title}</h3>
                        <p className="text-sm text-slate-500">
                          {new Date(appt.date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
                          {appt.time && ` at ${appt.time}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <div
                          onClick={(e) => downloadIcs(e, appt.id, appt.title)}
                          className="p-1.5 hover:bg-slate-100 rounded-lg"
                          title="Add to Calendar"
                          data-testid={`add-calendar-${appt.id}`}
                        >
                          <CalendarPlus size={16} className="text-slate-400 hover:text-[#235347]" weight="duotone" />
                        </div>
                        <CaretRight size={20} className="text-slate-400" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Past Appointments */}
            {pastAppts.length > 0 && (
              <div>
                <h2 className="text-sm font-medium text-slate-500 uppercase tracking-wide mb-3">Past</h2>
                <div className="space-y-3">
                  {pastAppts.slice(0, 5).map((appt) => (
                    <button
                      key={appt.id}
                      onClick={() => navigate(`/appointments/${appt.id}`)}
                      className="w-full card-base p-4 text-left hover:bg-slate-50 transition-colors flex items-center justify-between opacity-75"
                    >
                      <div>
                        <h3 className="font-medium text-slate-700">{appt.title}</h3>
                        <p className="text-sm text-slate-500">
                          {new Date(appt.date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
                          <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${getStatusColor(appt.status)}`}>
                            {appt.status}
                          </span>
                        </p>
                      </div>
                      <CaretRight size={20} className="text-slate-400" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {appointments.length === 0 && (
              <div className="text-center py-12">
                <CalendarBlank size={48} className="mx-auto text-slate-300 mb-4" />
                <h3 className="font-medium text-slate-900 mb-2">You're clear for now</h3>
                <p className="text-slate-500 text-sm">When something comes up, add it here and I'll help you prepare.</p>
              </div>
            )}
          </>
        ) : (
          /* Calendar View */
          <div className="card-base p-4">
            <div className="flex items-center justify-between mb-4">
              <button 
                onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <CaretLeft size={20} />
              </button>
              <h2 className="font-medium text-slate-900">
                {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </h2>
              <button 
                onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <CaretRight size={20} />
              </button>
            </div>
            
            <div className="grid grid-cols-7 gap-1 text-center text-xs text-slate-500 mb-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                <div key={d} className="py-2">{d}</div>
              ))}
            </div>
            
            <div className="grid grid-cols-7 gap-1">
              {Array(firstDay).fill(null).map((_, i) => (
                <div key={`empty-${i}`} className="aspect-square" />
              ))}
              {Array(daysInMonth).fill(null).map((_, i) => {
                const day = i + 1;
                const dayAppts = getAppointmentsForDay(day);
                const isToday = new Date().toDateString() === new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day).toDateString();
                
                return (
                  <div
                    key={day}
                    className={`aspect-square p-1 rounded-lg text-sm ${isToday ? 'bg-[#235347]/10 font-medium' : ''}`}
                  >
                    <div className="text-center">{day}</div>
                    {dayAppts.length > 0 && (
                      <div className="flex justify-center mt-1">
                        <div className={`w-2 h-2 rounded-full ${dayAppts[0].status === 'upcoming' ? 'bg-blue-500' : 'bg-green-500'}`} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Add Button */}
        <button
          data-testid="add-appointment"
          onClick={openNewModal}
          className="fixed bottom-24 right-6 w-14 h-14 bg-[#235347] text-white rounded-full shadow-lg flex items-center justify-center hover:bg-[#1A3F36] transition-colors"
        >
          <Plus size={24} />
        </button>
      </div>

      {/* Add Appointment Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-[70] flex items-end"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white w-full rounded-t-3xl px-6 pt-6 max-h-[90vh] overflow-auto z-[80]"
              style={{ paddingBottom: 'calc(120px + env(safe-area-inset-bottom, 0px))' }}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-medium text-slate-900">New Appointment</h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-slate-400 hover:text-slate-600 min-w-[44px] min-h-[44px] flex items-center justify-center -mr-2 rounded-full hover:bg-slate-100"
                  data-testid="appointment-modal-close"
                  aria-label="Close"
                >
                  <X size={24} />
                </button>
              </div>

              {/* Wave 2 P3 — Contextual hint. Only renders when we have a
                  confident specialty match from the user's record. The hint
                  also explains why fields below were prefilled. */}
              {specialtyHint && (
                <div
                  className="-mt-3 mb-5 flex items-start gap-2 rounded-xl bg-[#235347]/[0.06] border border-[#235347]/15 px-3.5 py-2.5"
                  data-testid="appointment-context-hint"
                >
                  <Sparkle size={14} weight="fill" className="text-[#235347] mt-[2px] flex-shrink-0" />
                  <p className="text-[12.5px] text-slate-700 leading-snug">
                    {(() => {
                      const s = specialtyHint.specialty;
                      const article = /^[aeiouAEIOU]/.test(s) ? 'an' : 'a';
                      return (
                        <>
                          Based on your record, this looks like {article} <span className="font-semibold text-[#0E1C2E]">{s.toLowerCase()}</span> follow-up.
                        </>
                      );
                    })()}
                    {' '}<span className="text-slate-500">Specialty and conditions prefilled — edit if not.</span>
                  </p>
                </div>
              )}

              <div className="space-y-4">
                <input
                  placeholder="Appointment title *"
                  value={formData.title || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-[#235347]/20"
                  data-testid="appointment-title-input"
                />
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="date"
                    value={formData.date || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none"
                    data-testid="appointment-date-input"
                  />
                  <input
                    type="time"
                    value={formData.time || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, time: e.target.value }))}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none"
                    data-testid="appointment-time-input"
                  />
                </div>
                <div>
                  {teamMembers.length > 0 && (
                    <select
                      value=""
                      onChange={(e) => {
                        const member = teamMembers.find(m => m.id === e.target.value);
                        if (member) {
                          setFormData(prev => ({
                            ...prev,
                            clinician_name: member.name,
                            clinician_id: member.id,
                            clinic_name: prev.clinic_name || member.clinic_name || '',
                            specialty: prev.specialty || member.role || '',
                          }));
                        }
                      }}
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none mb-2 text-sm"
                      data-testid="appointment-team-select"
                    >
                      <option value="">Pick from your care team...</option>
                      {teamMembers.map(m => (
                        <option key={m.id} value={m.id}>
                          {m.name}{m.role ? ` (${m.role})` : ''}
                        </option>
                      ))}
                    </select>
                  )}
                  <input
                    placeholder="Clinician/Doctor name"
                    value={formData.clinician_name || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, clinician_name: e.target.value, clinician_id: '' }))}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none"
                    data-testid="appointment-clinician-input"
                  />
                </div>
                <input
                  placeholder="Clinic/Hospital"
                  value={formData.clinic_name || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, clinic_name: e.target.value }))}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none"
                  data-testid="appointment-clinic-input"
                />
                <select
                  value={formData.specialty || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, specialty: e.target.value }))}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none"
                  data-testid="appointment-specialty-select"
                >
                  <option value="">Select specialty</option>
                  <option value="General Practice">General Practice</option>
                  <option value="Cardiology">Cardiology</option>
                  <option value="Neurology">Neurology</option>
                  <option value="Psychiatry">Psychiatry</option>
                  <option value="Dermatology">Dermatology</option>
                  <option value="Endocrinology">Endocrinology</option>
                  <option value="Gastroenterology">Gastroenterology</option>
                  <option value="Rheumatology">Rheumatology</option>
                  <option value="Oncology">Oncology</option>
                  <option value="Other">Other</option>
                </select>
                <textarea
                  placeholder="Purpose of visit"
                  value={formData.purpose || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, purpose: e.target.value }))}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none min-h-[80px] resize-none"
                />
                {conditions.length > 0 && (
                  <div>
                    <label className="text-sm text-slate-500 mb-2 block">Link to conditions</label>
                    <div className="flex flex-wrap gap-2">
                      {conditions.map(c => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => {
                            const current = formData.linked_conditions || [];
                            if (current.includes(c.name)) {
                              setFormData(prev => ({ ...prev, linked_conditions: current.filter(n => n !== c.name) }));
                            } else {
                              setFormData(prev => ({ ...prev, linked_conditions: [...current, c.name] }));
                            }
                          }}
                          className={`px-3 py-1 rounded-full text-sm ${
                            (formData.linked_conditions || []).includes(c.name)
                              ? 'bg-[#235347] text-white'
                              : 'bg-slate-100 text-slate-600'
                          }`}
                        >
                          {c.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

                <button
                  onClick={handleCreate}
                  disabled={!formData.title || !formData.date || saving}
                  className="w-full mt-6 bg-[#235347] text-white rounded-full px-6 py-3 font-medium hover:bg-[#1A3F36] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="create-appointment-btn"
                >
                  {saving && <Spinner size={16} className="animate-spin" />}
                  Create Appointment
                </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </MainLayout>
  );
};


export default AppointmentsPage;
